import { Component, Input, OnInit, OnDestroy , signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { forkJoin, Observable, Subscription, lastValueFrom, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { WellConfigService } from '../../../shared/services/well-config.service';
import { WellheadService } from '../../services/wellhead.service';
import { WellheadModel } from './wellhead.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { GetForceValueFromLbf, GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'app-wellhead',
  templateUrl: './wellhead.component.html',
  standalone: false
})
export class WellheadComponent implements OnInit, OnDestroy {

  private _destroy$: Subject<void> = new Subject<void>();
  private _subscriptions: Subscription = new Subscription();
  private _wellheadBackup: WellheadModel;
  public wellheadForm: FormGroup;
  public wellheadDisabled: boolean;
  public innerDiameterUnit: string;
  public forceUnit: string;
  public isLoading = signal<boolean>(false);
  public configMissing: boolean;

  // State
  @Input()
  private componentId: string;

  public get wellheadInnerDiameterControl(): UntypedFormControl {
    return this.wellheadForm.get('wellheadInnerDiameter') as UntypedFormControl;
  }

  public get hangerLockdownRatingControl(): UntypedFormControl {
    return this.wellheadForm.get('hangerLockdownRating') as UntypedFormControl;
  }

  constructor(
    private _store: StoreService,
    private _formBuilder: FormBuilder,
    private _signalRService: SignalRService,
    private _wellheadService: WellheadService,
    private _wellConfigService: WellConfigService,
    private _messenger: MediatorService
  ) {
    this.wellheadForm = this._formBuilder.group({
      tubularStringId: new FormControl(null),
      wellheadInnerDiameter: new FormControl({ value: null, disabled: this.wellheadDisabled }, { validators: Validators.required, updateOn: 'blur' }),
      hangerLockdownRating: new FormControl({ value: null, disabled: this.wellheadDisabled }, { validators: Validators.required, updateOn: 'blur' })
    });
  }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    const units = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);
    this.innerDiameterUnit = units.shortLengths;
    this.forceUnit = units.force;

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.getData();
    
    this._subscriptions.add(this.wellheadForm.valueChanges.subscribe(v => {
      if (!isEqual(v, this._wellheadBackup)) {
        this._wellheadBackup = v;
        this.onFormChange(v);
      }
    }));
  }

  signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.isLoading.set(true);
      this.getData();
    }
  }

  private getData() {
    this._wellConfigService.getTubular().subscribe({
      next: (tubular) => {
        if (tubular != null) {

          this._store.watchSession(SessionStorageKeys.APP_IDS)
          .pipe(takeUntil(this._destroy$))
          .subscribe({
            next: (appIds: CurrentAppIdsDto) => {
              if (appIds?.tubularId !== undefined) {
                const sources: Observable<any>[] = [
                  this._wellheadService.getWellheadForTubular()
                ];

                forkJoin(sources).pipe(
                  map(([wellhead]) => {
                    this.wellheadForm.controls.tubularStringId.setValue(appIds.tubularId, { emitEvent: false });
                    this.wellheadDisabled = tubular.sequenceNumber === 0 || tubular.type === 'Liner';

                    if (this.wellheadDisabled) {
                      this.wellheadForm.controls.wellheadInnerDiameter.disable({ emitEvent: false });
                      this.wellheadForm.controls.hangerLockdownRating.disable({ emitEvent: false });
                    } else {
                      this.wellheadForm.controls.wellheadInnerDiameter.enable({ emitEvent: false });
                      this.wellheadForm.controls.hangerLockdownRating.enable({ emitEvent: false });
                    }

                    if (!wellhead.id) {
                      const wellheadDefault = wellhead.currentStringOd < GetShortLengthValueFromInches(18.625, this.innerDiameterUnit) ?
                        GetShortLengthValueFromInches(18.75, this.innerDiameterUnit) : GetShortLengthValueFromInches(28, this.innerDiameterUnit);

                      this.wellheadForm.controls.wellheadInnerDiameter.patchValue(wellheadDefault);
                      this.wellheadForm.controls.hangerLockdownRating.patchValue(GetForceValueFromLbf(500000, this.forceUnit));
                      this.wellheadForm.controls.hangerLockdownRating.markAsDirty();
                      this.onFormChange(this.wellheadForm.value, true);
                    } else {
                      this.wellheadForm.patchValue(wellhead, { emitEvent: false });
                    }
                    this._wellheadBackup = this.wellheadForm.value;

                    this.isLoading.set(false);
                  }),
                  catchError(err => {
                    this.isLoading.set(false);
                    return err;
                  })
                ).subscribe();
              } else {
                this.configMissing = true;
                this.wellheadDisabled = false;
                this.isLoading.set(false);
                return;
              }
            },
            error: () => {
              this.isLoading.set(false);
            }
          });
      } else {
        this.configMissing = true;
        this.wellheadDisabled = false;
        this.isLoading.set(false);
        return;
      }
    }});
  }

  private async onFormChange(formData: WellheadModel, isOnInit?: boolean) {
    if (this.wellheadForm.controls.hangerLockdownRating.pristine && this.wellheadForm.controls.wellheadInnerDiameter.pristine) {
      return;
    }
    if (this.wellheadForm.valid && (!this.isLoading() || isOnInit)) {
      await lastValueFrom(this._wellheadService.updateWellheadForTubular(formData));
      this._messenger.publish(new DataSavedMessage('Wellhead'));
    }
  }

  get isDialogVisible(): boolean {
    return this.wellheadDisabled || this.configMissing;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this.signalRfunc = null;
    this._destroy$.next(); // Emit a value to trigger unsubscription
    this._destroy$.complete(); // Complete the subject to clean up
  }

}
